import { useState } from "react";
import classnames from "classnames";
import HumanMessage from "duck/ui/chat/components/messages/HumanMessage";
import SafeModeLabel from "duck/ui/chat/components/messages/SafeModeLabel";
import SubthreadMessage from "duck/ui/chat/components/messages/SubthreadMessage";
import IntermediateOutputMessage from "duck/ui/chat/components/messages/ToolCallMessage/IntermediateOutputMessage";
import MessageContentRenderer from "duck/ui/chat/components/renderers/MessageContentRenderer";
import StatusMessageRenderer from "duck/ui/chat/components/renderers/StatusMessageRenderer";
import { ChatMessage, ChatMessageRole } from "duck/ui/chat/types";
import { shouldRenderMessage } from "duck/ui/chat/utils";
import { useTranslation } from "react-i18next";
import StopIcon from "@mui/icons-material/Stop";
import Box from "@mui/material/Box";

import { useViewport } from "shared/contexts/ViewportContext";

import Typography from "features/components/Typography";

interface MessagesProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  // optional: index of the last user message for scroll ref (used in main chat)
  lastUserMessageIndex?: number;
  // optional: ref to attach to the last user message (used in main chat)
  mostRecentUserMessageRef?: React.RefObject<HTMLDivElement | null>;
  // optional: override thread ID for subthreads (used for feedback)
  threadId?: string;
  // optional: indicates this is a subthread context (changes how human messages are displayed)
  isSubthread?: boolean;
}

/**
 * Reusable component for rendering a list of chat messages.
 * Used by both ChatMessages (main thread) and SubthreadMessages (subthreads).
 */
const Messages = ({
  messages,
  isStreaming,
  lastUserMessageIndex,
  mostRecentUserMessageRef,
  threadId,
  isSubthread = false,
}: MessagesProps) => {
  const { t } = useTranslation("duck");
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const { isMobile } = useViewport();
  const lastAIMessageIndex = messages.reduce(
    (lastIndex, message, index) =>
      message.role === ChatMessageRole.AI ? index : lastIndex,
    -1
  );

  const onCopy = () => {
    if (window.heap) {
      window.heap.track("Duck Message Copied");
    }
  };

  return (
    <>
      {messages.map((message, index) => {
        if (!shouldRenderMessage(message)) {
          return null;
        }

        return (
          <Box
            // Key on the server id once known so the optimistic→server id swap
            // (post-stream refetch) doesn't remount the bubble and wipe in-progress
            // feedback (selected thumb / open comment box).
            key={message.serverId ?? message.id}
            ref={
              lastUserMessageIndex !== undefined &&
              index === lastUserMessageIndex
                ? mostRecentUserMessageRef
                : null
            }
            className={classnames({
              "text-base": isMobile,
              "text-sm": !isMobile,
            })}
            onCopy={onCopy}
          >
            {message.role === ChatMessageRole.HUMAN ? (
              <HumanMessage message={message} isSubthread={isSubthread} />
            ) : message.role === ChatMessageRole.TOOL_CALL ? (
              <IntermediateOutputMessage
                message={message}
                isStreaming={isStreaming}
              />
            ) : message.role === ChatMessageRole.TOOL_RESPONSE ? (
              <SubthreadMessage message={message} />
            ) : (
              <Box
                className="w-full flex justify-start"
                onMouseEnter={() =>
                  setHoveredMessageId(message.serverId ?? message.id)
                }
                onMouseLeave={() => setHoveredMessageId(null)}
              >
                <Box className="w-full py-0.5 text-left text-gray-700 wrap-anywhere">
                  {message.safeMode && <SafeModeLabel />}
                  <MessageContentRenderer
                    message={message}
                    isStreaming={isStreaming}
                    isHovered={
                      hoveredMessageId === (message.serverId ?? message.id)
                    }
                    isLastAIMessage={index === lastAIMessageIndex}
                    threadId={threadId}
                  />

                  {message.aborted && (
                    <Typography
                      variant="caption"
                      className="flex items-center gap-0.5 mt-1"
                    >
                      <StopIcon fontSize="small" />
                      {t("stopped")}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            <StatusMessageRenderer
              currentlyStreaming={isStreaming}
              message={message}
            />
          </Box>
        );
      })}
    </>
  );
};

export default Messages;
